'use client';

import clsx from 'clsx';

import { Loading } from '../loading';
import React, { memo } from 'react';
import { AiIcon } from './ai-icon';
import { Tooltip } from '../tooltip';

export type ButtonTheme =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'danger-secondary'
  | 'overlay'
  | 'ghost';

export type ButtonStyle = 'solid' | 'outline';

export type ButtonSize = 'xs' | 'small' | 'medium' | 'large';

export type ButtonProps = {
  enterprise?: boolean;
  label: React.ReactNode;
  /**
   * Accessible name for the button. Required in practice for `iconOnly`
   * buttons, where `label` is not rendered; falls back to a string `label`
   * and then to `tooltip`.
   */
  ariaLabel?: string;
  theme?: ButtonTheme;
  style?: ButtonStyle;
  size?: ButtonSize;
  full?: boolean;
  flat?: boolean;
  busy?: boolean;
  disabled?: boolean;
  loading?: boolean;
  iconOnly?: boolean;
  arrowLeft?: boolean;
  arrowRight?: boolean;
  iconLeft?: string;
  selectIcon?: boolean;
  iconRight?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  className?: string;
  truncateLabel?: boolean;
  id?: string;
  pill?: boolean;
  ai?: boolean;
  type?: 'button' | 'submit' | 'reset';
  tooltip?: string;
  tooltipDisabled?: boolean;
  tooltipDirection?: 'top' | 'bottom' | 'left' | 'right';
  children?: React.ReactNode;
  loadingMessage?: string;
  iconFile?: any;
  noHover?: boolean;
  'data-test-id'?: string;
};

const ICON_SIZE_MAP: Record<
  ButtonSize,
  (params: {
    iconOnly?: boolean;
    isArrow?: boolean;
    selectIcon?: boolean;
  }) => string
> = {
  xs: ({ iconOnly, isArrow }) =>
    `text-sm ${
      iconOnly
        ? 'w-3.5 h-3.5'
        : isArrow
          ? 'w-[13px] h-[13px]'
          : 'w-[18px] h-[18px]'
    }`,
  small: ({ iconOnly, isArrow }) =>
    `text-sm ${
      iconOnly
        ? 'w-3.5 h-3.5'
        : isArrow
          ? 'w-[13px] h-[13px]'
          : 'w-[18px] h-[18px]'
    }`,
  medium: ({ iconOnly, isArrow, selectIcon }) =>
    `${
      iconOnly
        ? 'text-lg w-[18px] h-[18px]'
        : isArrow
          ? 'text-sm w-3.5 h-3.5'
          : selectIcon
            ? 'text-base w-[14px] h-[13px]'
            : 'text-sm w-5 h-5'
    }`,
  large: ({ iconOnly, isArrow, selectIcon }) =>
    `${
      iconOnly
        ? 'text-[22px] w-[22px] h-[22px]'
        : isArrow
          ? 'text-base w-4 h-4'
          : selectIcon
            ? 'text-[18px] w-[16px] h-[15px]'
            : 'text-base w-5 h-5'
    }`,
};

const ButtonIcon = ({
  size,
  disabled,
  isArrow,
  iconName,
  iconOnly,
  selectIcon,
  iconPadding,
  iconFile,
}: {
  size: ButtonSize;
  disabled?: boolean;
  isArrow?: boolean;
  iconName: string;
  iconOnly?: boolean;
  selectIcon?: boolean;
  iconPadding: boolean;
  iconFile?: any;
}) => {
  if (iconFile) {
    return iconFile;
  }

  const iconSize = ICON_SIZE_MAP[size]({ iconOnly, isArrow, selectIcon });

  return (
    <i
      className={`${iconName} flex! flex-none items-center justify-center leading-0! ${disabled ? 'text-input-disabled' : ''} ${iconSize} ${
        iconPadding ? 'mr-1' : ''
      }`}
    />
  );
};

const ButtonLabel = ({
  label,
  truncate,
  disabled,
}: {
  label: React.ReactNode;
  truncate?: boolean;
  disabled?: boolean;
}) => {
  return (
    <span
      className={clsx(
        'px-1',
        truncate && 'max-w-full truncate text-left',
        disabled && 'text-input-disabled',
      )}
    >
      {label}
    </span>
  );
};

const ButtonInner = ({
  ref,
  id,
  label,
  ariaLabel,
  size = 'small',
  theme,
  style = 'solid',
  flat = false,
  full = false,
  busy = false,
  pill = false,
  disabled = false,
  loading,
  iconOnly,
  selectIcon,
  arrowLeft,
  arrowRight,
  iconLeft,
  iconFile,
  iconRight,
  onClick,
  className = '',
  ai = false,
  tooltip,
  tooltipDisabled,
  tooltipDirection = 'bottom',
  type = 'button',
  children,
  loadingMessage,
  truncateLabel,
  noHover = false,
  'data-test-id': dataTestId,
}: ButtonProps & {
  ref?: React.RefObject<HTMLButtonElement | null>;
}) => {
  const [hover, setHover] = React.useState(false);

  // `iconOnly` drops the label from the DOM, so without this the button has no
  // accessible name at all — a tooltip is not exposed as one.
  const accessibleName =
    ariaLabel ??
    (iconOnly ? (typeof label === 'string' ? label : tooltip) : undefined);

  const getClassNames = () => {
    let classNames = `relative flex items-center justify-center font-bold uppercase focus:ring-0 focus:outline-hidden transition transition-200 ${
      flat
        ? `text-gray-900 focus:bg-button-text-focus active:bg-button-text-active ${noHover ? '' : 'hover:bg-button-text-hover'}`
        : 'disabled:bg-input-bg-disabled disabled:text-input-disabled disabled:cursor-not-allowed'
    } ${full ? 'w-full!' : ''} ${
      ai && loading ? ' animate-[loading_2s_infinite] ' : ''
    } ${ai && !loading ? ' hover:animate-[loading_2s_infinite] ' : ''}`;

    switch (theme) {
      case 'ghost':
        classNames += ` bg-transparent text-primary-600 hover:bg-transparent focus:bg-transparent active:bg-transparent disabled:bg-transparent !disabled:cursor-default`;
        break;
      case 'secondary':
        classNames += ` ${
          !flat
            ? 'bg-surface-2 disabled:text-input-disabled disabled:bg-transparent bg-transparent disabled:border-input-disabled disabled:border'
            : 'bg-transparent'
        } text-gray-900 border-solid border border-button-secondary-stroke focus:bg-button-secondary-hover active:bg-button-secondary-active ${noHover ? '' : 'hover:bg-button-secondary-hover'}`;
        break;

      case 'danger-secondary':
        classNames += ` ${
          !flat
            ? 'bg-surface-2 disabled:text-input-disabled disabled:bg-transparent disabled:border-input-disabled disabled:border'
            : 'bg-transparent'
        } text-red-600! border-solid border border-red-600! focus:bg-button-secondary-hover active:bg-button-secondary-active ${noHover ? '' : 'hover:bg-button-secondary-hover'}`;
        break;

      case 'overlay':
        classNames += ` shadow-md bg-surface-overlay text-side-nav-label focus:bg-button-overlay-focus active:bg-button-overlay-active ${noHover ? '' : 'hover:bg-button-overlay-hover'}`;
        break;

      case 'danger':
        classNames +=
          ' bg-red-600 text-white hover:bg-red-500 disabled:border-0 active:bg-red-700 focus:bg-red-500 disabled:text-neutral-300';
        break;
      default:
        classNames += `${
          !flat
            ? `border-0 bg-primary-600 active:bg-primary-700 focus:bg-primary-500 disabled:text-neutral-300 disabled:border-0 text-white ${noHover ? '' : 'hover:bg-primary-500'}`
            : 'bg-transparent'
        }`;
    }

    switch (style) {
      case 'outline':
        classNames += ` bg-transparent! border-solid! border! `;
        if (theme === 'danger') {
          classNames += ` text-red-600! border-red-600! active:bg-red-100! focus:bg-red-50! ${noHover ? '' : 'hover:bg-red-50!'}`;
        }
        if (theme === 'secondary') {
          classNames += ` text-gray-900! border-button-secondary-stroke! focus:bg-button-secondary-hover! active:bg-button-secondary-active! ${noHover ? '' : 'hover:bg-button-secondary-hover!'}`;
        }
        break;
      case 'solid':
      default:
        break;
    }

    switch (size) {
      case 'xs':
        classNames += ` px-2 py-1 min-h-6 rounded-md text-[11px] ${
          iconOnly ? 'w-6' : ''
        }`;
        break;
      case 'small':
        classNames += ` px-2 py-1 min-h-8 leading-4 rounded-md text-xs ${
          iconOnly ? 'w-8' : ''
        }`;
        break;
      case 'large':
        classNames += ` px-5 py-1 min-h-12 leading-6 rounded-md text-base ${
          iconOnly ? 'w-12' : ''
        }`;
        break;
      case 'medium':
      default:
        classNames += ` px-3 py-2.5 h-10 leading-5 rounded-md text-[14px] ${
          iconOnly ? 'w-10' : ''
        }`;
    }

    if (pill) {
      classNames += ' rounded-full!';
    }
    if (selectIcon) {
      classNames += ' rounded-l-none';
    }

    return classNames;
  };

  const internalRef = React.useRef<HTMLButtonElement | null>(null);
  const buttonRef = ref ?? internalRef;

  return (
    <button
      id={id}
      ref={buttonRef}
      type={type}
      aria-label={accessibleName}
      data-busy={busy.toString()}
      data-test-id={dataTestId}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={
        ai && !disabled
          ? {
              backgroundImage:
                'linear-gradient( 80deg, var(--color-primary-600) 0%, var(--color-primary-600) 10%, #b3241a 50%, #a63cac 70%, var(--color-primary-600) 90%, var(--color-primary-600) 100% )',
              backgroundPosition: 'left',
              backgroundSize: '800%',
            }
          : {}
      }
      className={`${getClassNames()} ${className ?? ''}`}
      onClick={onClick}
      disabled={disabled || false}
    >
      {children}
      {loading && !ai ? (
        <>
          <Loading
            size="small"
            className="m-auto"
            color="#FFFFFF"
            type="bold"
          />
          {loadingMessage && (
            <ButtonLabel disabled={disabled} label={loadingMessage} />
          )}
        </>
      ) : (
        <>
          {arrowLeft && (
            <ButtonIcon
              size={size}
              iconName="fas fa-angle-left"
              iconPadding={!iconOnly}
              iconOnly={iconOnly}
              isArrow
            />
          )}
          {ai && (
            <AiIcon
              loading={loading}
              color={disabled ? 'fill-input-disabled' : 'fill-neutral-0'}
            />
          )}
          {!ai && iconLeft && (
            <ButtonIcon
              size={size}
              disabled={disabled}
              iconName={iconLeft}
              iconPadding={!iconOnly}
              iconOnly={iconOnly}
              iconFile={iconFile}
            />
          )}
          {!iconOnly && label && (
            <ButtonLabel
              disabled={disabled}
              label={label}
              truncate={truncateLabel}
            />
          )}
          {selectIcon && (
            <ButtonIcon
              size={size}
              iconPadding={!iconOnly}
              iconName="fas fa-angle-down"
              selectIcon
            />
          )}
          {iconRight && (
            <ButtonIcon
              size={size}
              iconName={iconRight}
              iconPadding={false}
              iconOnly={iconOnly}
            />
          )}
          {arrowRight && (
            <ButtonIcon
              size={size}
              iconName="fas fa-angle-right"
              iconPadding={!iconOnly}
              iconOnly={iconOnly}
              isArrow
            />
          )}
        </>
      )}
      {!tooltipDisabled && (
        <Tooltip
          show={!!(tooltip && hover)}
          direction={tooltipDirection}
          targetRef={buttonRef}
        >
          {tooltip}
        </Tooltip>
      )}
    </button>
  );
};

ButtonInner.displayName = 'ButtonInner';

export const Button = memo(ButtonInner);
